import { DataTable } from "mantine-datatable";
import React, { useEffect, useMemo, useState } from "react";
import CountriesService from "../../service/countries.service";
import isoCountries from "../../components/countries.json";
import Swal from "sweetalert2";

/**
 * Countries — cross-check coverage control.
 *
 * Shows EVERY ISO country (not only ones we've seen an install from) so coverage can be
 * PRE-enabled ahead of the first install from a country. Cross-check is the PAID
 * verification fallback (used when a SIM can receive but not originate SMS, e.g. roaming);
 * it fires ONLY where enabled here. Enabling a never-seen country creates its row on the
 * backend. Supports search, multi-select, and enable/disable-all (of the filtered set).
 */
function Countries() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]); // backend Country rows (state for known countries)
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]); // array of ISO codes
  const [busy, setBusy] = useState(false);

  const toast = (icon, title) =>
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
      customClass: { container: "toast" },
    }).fire({ icon, title, padding: "10px 20px" });

  const load = async (spin = true) => {
    if (spin) setIsLoading(true);
    try {
      const response = await CountriesService.getCountries();
      setRows(response?.data?.apiresponse?.data || []);
    } catch (error) {
      toast("error", "Couldn't load countries");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(true);
  }, []);

  // Merge the full ISO list with backend state (matched by code, case-insensitive).
  const merged = useMemo(() => {
    const byCode = {};
    rows.forEach((r) => {
      if (r.country) byCode[String(r.country).toUpperCase()] = r;
    });
    return isoCountries.map((c) => {
      const state = byCode[c.value.toUpperCase()];
      return {
        code: c.value,
        name: c.label,
        enabled: !!(state && state.crossCheckEnabled),
        sent: (state && state.crossCheckSent) || 0,
        dailySent: (state && state.crossCheckDailySent) || 0,
        quota: (state && state.crossCheckDailyQuota) || 0, // 0 = system default
        verifyMode: (state && state.verifyMode) || "SELF_ONLY",
        virtualNumber: (state && state.virtualNumber) || "",
        maxCrossPerDevice: (state && state.maxCrossPerDevice) || 0, // 0 = system default (3)
        providers: (state && state.provider) || [],
        known: !!state,
      };
    });
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [merged, query]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selected.includes(c.code));

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const codes = new Set(filtered.map((c) => c.code));
      setSelected((prev) => prev.filter((c) => !codes.has(c)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...filtered.map((c) => c.code)])));
    }
  };

  const toggleOne = async (row) => {
    setBusy(true);
    try {
      await CountriesService.setCrossCheck(row.code, !row.enabled);
      toast("success", `Cross-check ${!row.enabled ? "enabled" : "disabled"} for ${row.code}`);
      await load(false);
    } catch (e) {
      toast("error", "Update failed");
    } finally {
      setBusy(false);
    }
  };

  // Bulk over a set of codes. `codes` defaults to the current selection.
  const bulk = async (enabled, codes) => {
    const list = codes || selected;
    if (!list.length) {
      toast("error", "Select at least one country");
      return;
    }
    setBusy(true);
    try {
      const res = await CountriesService.setCrossCheckBulk(list, enabled);
      const n = res?.data?.apiresponse?.data?.updated ?? list.length;
      toast("success", `${enabled ? "Enabled" : "Disabled"} cross-check for ${n} countries`);
      setSelected([]);
      await load(false);
    } catch (e) {
      toast("error", "Bulk update failed");
    } finally {
      setBusy(false);
    }
  };

  const editQuota = async (row) => {
    const { value } = await Swal.fire({
      title: `Daily quota — ${row.code}`,
      input: "number",
      inputLabel: "Max cross-check sends per day (0 = system default of 200)",
      inputValue: row.quota || 0,
      showCancelButton: true,
      confirmButtonText: "Save",
      inputAttributes: { min: 0 },
    });
    if (value === undefined) return; // cancelled
    setBusy(true);
    try {
      await CountriesService.setQuota(row.code, parseInt(value, 10) || 0);
      toast("success", `Quota set for ${row.code}`);
      await load(false);
    } catch (e) {
      toast("error", "Quota update failed");
    } finally {
      setBusy(false);
    }
  };

  const changeMode = async (row, mode) => {
    if (mode === row.verifyMode) return;
    setBusy(true);
    try {
      await CountriesService.setVerifyMode(row.code, mode);
      toast("success", `Auth mode ${mode} for ${row.code}`);
      await load(false);
    } catch (e) {
      toast("error", "Mode update failed");
    } finally {
      setBusy(false);
    }
  };

  const editVirtual = async (row) => {
    const { value } = await Swal.fire({
      title: `Virtual number — ${row.code}`,
      input: "text",
      inputLabel: "MailBIT number for SELF_TO_VIRTUAL (digits only; blank = global default)",
      inputValue: row.virtualNumber || "",
      showCancelButton: true,
      confirmButtonText: "Save",
      inputPlaceholder: "66977063200",
    });
    if (value === undefined) return; // cancelled
    setBusy(true);
    try {
      await CountriesService.setVirtualNumber(row.code, (value || "").replace(/[^0-9]/g, ""));
      toast("success", `Virtual number ${value ? "set" : "cleared"} for ${row.code}`);
      await load(false);
    } catch (e) {
      toast("error", "Virtual number update failed");
    } finally {
      setBusy(false);
    }
  };

  const editMaxCross = async (row) => {
    const { value } = await Swal.fire({
      title: `Max cross-check / device — ${row.code}`,
      input: "number",
      inputLabel: "Lifetime cross-checks allowed per DEVICE, across all numbers (0 = system default of 3)",
      inputValue: row.maxCrossPerDevice || 0,
      showCancelButton: true,
      confirmButtonText: "Save",
      inputAttributes: { min: 0 },
    });
    if (value === undefined) return;
    setBusy(true);
    try {
      await CountriesService.setMaxCrossPerDevice(row.code, parseInt(value, 10) || 0);
      toast("success", `Max cross/device set for ${row.code}`);
      await load(false);
    } catch (e) {
      toast("error", "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const configuredCount = merged.filter((c) => c.verifyMode && c.verifyMode !== "SELF_ONLY").length;
  // The 7 per-country auth modes (order: free self-loop → SIM-read variants → virtual → paid cross).
  const MODES = ["SELF_ONLY", "SL_SIM", "SELF_AND_CROSS", "SL_SIM_CROSS", "SL_SIM_VLN", "SELF_TO_VIRTUAL", "CROSS_ONLY"];
  const MODE_LABELS = {
    SELF_ONLY: "Self-loop · any number",
    SL_SIM: "Self-loop · SIM-read only",
    SELF_AND_CROSS: "Self-loop + Cross",
    SL_SIM_CROSS: "SIM-read + Cross",
    SL_SIM_VLN: "SIM-read + Virtual",
    SELF_TO_VIRTUAL: "Virtual number (VLN)",
    CROSS_ONLY: "Cross only",
  };

  const modeDot = (m) =>
    m === "SELF_TO_VIRTUAL" || m === "SL_SIM_VLN" ? "#00253F"
      : m === "CROSS_ONLY" || m === "SELF_AND_CROSS" || m === "SL_SIM_CROSS" ? "#e2a03f"
      : m === "SL_SIM" ? "#00ab55"
      : "#9aa0a6";
  const chip =
    "inline-flex items-center rounded border border-gray-200 dark:border-[#2a2a2d] px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:border-primary hover:text-[#1a1a1f] dark:hover:text-white transition-colors";

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2
            className="text-2xl font-extrabold text-[#1a1a1f] dark:text-white-light"
            style={{ fontFamily: "Archivo, system-ui, sans-serif", letterSpacing: "-.02em" }}
          >
            Countries
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Per-country verification policy. <b>Auth mode</b> is the single switch (self-loop / cross / MO).
            <b> Max cross / device</b> caps the paid cross-check per device. <b> Virtual #</b> is the
            SELF_TO_VIRTUAL destination.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded bg-primary text-white font-semibold text-xs px-2.5 py-1 whitespace-nowrap">
          {configuredCount} configured
        </span>
      </div>

      <div className="relative w-full sm:w-80">
        <svg className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" />
        </svg>
        <input
          type="text"
          placeholder="Search country or code…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-lg border border-gray-200 dark:border-[#2a2a2d] bg-white dark:bg-[#0e1726] ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-[#2a2a2d] bg-white dark:bg-[#0e1726] shadow-sm overflow-hidden">
        <DataTable
          fetching={isLoading}
          noRecordsText="No countries"
          highlightOnHover
          className="whitespace-nowrap min-w-[760px]"
          records={filtered}
          idAccessor="code"
          columns={[
            {
              accessor: "name",
              title: "Country",
              render: ({ name, code }) => (
                <span className="font-semibold">
                  {name} <span className="text-gray-400 !text-xs font-normal">({code})</span>
                </span>
              ),
            },
            {
              accessor: "verifyMode",
              title: "Auth mode",
              render: (row) => (
                <div className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: modeDot(row.verifyMode) }} />
                  <select
                    value={row.verifyMode}
                    disabled={busy}
                    onChange={(e) => changeMode(row, e.target.value)}
                    className="rounded-lg border border-gray-200 dark:border-[#2a2a2d] bg-white dark:bg-[#0e1726] px-2.5 py-1.5 text-xs font-medium outline-none focus:border-primary focus:ring-1 focus:ring-primary cursor-pointer"
                    title="Verification policy for this country (the single switch)"
                  >
                    {MODES.map((m) => (
                      <option key={m} value={m}>{MODE_LABELS[m] || m}</option>
                    ))}
                  </select>
                </div>
              ),
            },
            {
              accessor: "maxCrossPerDevice",
              title: "Max cross / device",
              render: (row) => (
                <button type="button" onClick={() => editMaxCross(row)} className={chip}
                  title="Lifetime cross-checks per device, across all numbers. Click to set.">
                  {row.maxCrossPerDevice > 0 ? row.maxCrossPerDevice : "3"}
                  <span className="text-gray-400 ltr:ml-1 rtl:mr-1">/dev</span>
                </button>
              ),
            },
            {
              accessor: "virtualNumber",
              title: "Virtual #",
              render: (row) => (
                <button type="button" onClick={() => editVirtual(row)}
                  className={chip + " !font-mono"}
                  title="Click to set the SELF_TO_VIRTUAL destination number">
                  {row.virtualNumber ? row.virtualNumber : "— default"}
                </button>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default Countries;
